import { baseApi } from "api/BaseApi";
import {
  PaginatedResponse,
  PaginatedQuery,
} from "api/common/types/response.types";
import { niapColorAdapter } from "utils/color";

import {
  AddUsersToListPayload,
  AddUserToListsPayload,
  CreateMailCategoryPayload,
  CreateMailListPayload,
  CreateMailUserPayload,
  UpdateMailUserPayload,
  DeleteBulkMailCategoryPayload,
  MailAccount,
  MailCategory,
  MailList,
  MailListCategoriesQuery,
  MailListMember,
  MailListQueryParams,
  MailListSummary,
  MailConfig,
  MailListUsersQuery,
  MailUserListsQuery,
  RecipientType,
  RemoveUsersFromListPayload,
  UpdateMailCategoryPayload,
  UpdateMailListPayload,
} from "./mailList.types";

/**
 * Raw membership row as returned by `/lists/{id}/users/`
 * (MailListMemberSerializer). Internal wire shape — flattened to
 * `MailListMember` before it reaches components.
 */
interface MailListMembershipResponse {
  mailAccount: MailAccount;
  mailList: MailListSummary;
  recipientType: RecipientType | null;
  dateAdded: MailListMember["dateAdded"];
  active: boolean;
  // The membership's OWN Exchange sync state (not the account's).
  syncStatus: MailListMember["syncStatus"];
  lastSyncError: MailListMember["lastSyncError"];
  lastSyncedAt: MailListMember["lastSyncedAt"];
}

/** Enriches a MailCategory with the resolved themeColor. */
function enrichCategory(category: MailCategory | null): MailCategory | null {
  if (!category) return null;
  return { ...category, themeColor: niapColorAdapter(category.color) };
}

/** Enriches a MailList's nested category. */
function enrichMailList<T extends { category: MailCategory | null }>(
  list: T,
): T {
  return { ...list, category: enrichCategory(list.category) };
}

/** Enriches a MailAccount's nested mailingLists categories. */
function enrichMailAccount(account: MailAccount): MailAccount {
  return {
    ...account,
    mailingLists: account.mailingLists.map(enrichMailList),
  };
}

/**
 * Flattens a membership envelope into a MailListMember: the account's fields
 * are hoisted to the top level (so the DataGrid can use the account id as the
 * row id and read name/email directly), with the membership's own metadata
 * alongside. The nested account's categories are enriched en route.
 *
 * NOTE: the sync fields (syncStatus/lastSyncError/lastSyncedAt) are taken from
 * the MEMBERSHIP, not the account — this view shows whether each account's
 * membership in this list is in sync with Exchange. The account spread comes
 * first so these overwrite the account's own sync fields.
 */
function flattenMember(row: MailListMembershipResponse): MailListMember {
  return {
    ...enrichMailAccount(row.mailAccount),
    recipientType: row.recipientType,
    dateAdded: row.dateAdded,
    membershipActive: row.active,
    syncStatus: row.syncStatus,
    lastSyncError: row.lastSyncError,
    lastSyncedAt: row.lastSyncedAt,
  };
}

const MAIL_BASE_URL = "v2/mail";

export const mailListApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // --- Mail Config ---

    // Environment-specific mail settings (available domains, etc.). The backend
    // is the source of truth — the UI sources domain options from here instead
    // of hardcoding them.
    getMailConfig: build.query<MailConfig, void>({
      query: () => `${MAIL_BASE_URL}/config/`,
      providesTags: ["MAIL_CONFIG"],
    }),

    // --- Mail Lists ---

    listMailLists: build.query<MailList[], MailListQueryParams>({
      query: (params) => ({
        url: `${MAIL_BASE_URL}/lists/`,
        params,
      }),
      transformResponse: (response: MailList[]) => response.map(enrichMailList),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "MAIL_LISTS" as const, id })),
              "MAIL_LISTS",
            ]
          : ["MAIL_LISTS"],
    }),

    getMailList: build.query<MailList, string>({
      query: (mailListId) => `${MAIL_BASE_URL}/lists/${mailListId}/`,
      transformResponse: (response: MailList) => enrichMailList(response),
      providesTags: (_result, _error, mailListId) => [
        { type: "MAIL_LISTS", id: mailListId },
      ],
    }),

    createMailList: build.mutation<MailList, CreateMailListPayload>({
      query: (body) => ({
        url: `${MAIL_BASE_URL}/lists/`,
        method: "POST",
        body,
      }),
      transformResponse: (response: MailList) => enrichMailList(response),
      invalidatesTags: ["MAIL_LISTS"],
    }),

    updateMailList: build.mutation<MailList, UpdateMailListPayload>({
      query: ({ id: mailListId, ...body }) => ({
        url: `${MAIL_BASE_URL}/lists/${mailListId}/`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: MailList) => enrichMailList(response),
      invalidatesTags: (result, error, { id: mailListId }) => [
        { type: "MAIL_LISTS", id: mailListId },
        { type: "MAIL_CATEGORIES" },
      ],
    }),

    deleteMailList: build.mutation<void, string>({
      query: (mailListId) => ({
        url: `${MAIL_BASE_URL}/lists/${mailListId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, mailListId) => [
        { type: "MAIL_LISTS", id: mailListId },
        "MAIL_LISTS",
        "MAIL_USER_LISTS",
        "MAIL_USERS",
      ],
    }),

    // --- Mail List Users (nested: /lists/{id}/users/) ---

    listMailListUsers: build.query<
      PaginatedResponse<MailListMember>,
      PaginatedQuery<MailListUsersQuery>
    >({
      query: ({ listId, ...params }) => ({
        url: `${MAIL_BASE_URL}/lists/${listId}/users/`,
        params,
      }),
      transformResponse: (
        response: PaginatedResponse<MailListMembershipResponse>,
      ) => ({
        ...response,
        results: response.results.map(flattenMember),
      }),
      providesTags: ["MAIL_LIST_USERS"],
    }),

    addUsersToMailList: build.mutation<MailListMember[], AddUsersToListPayload>(
      {
        query: ({ mailListId, ...body }) => ({
          url: `${MAIL_BASE_URL}/lists/${mailListId}/users/`,
          method: "POST",
          body,
        }),
        // Response mirrors the GET shape (membership rows) — flatten to
        // MailListMember. The modal ignores the returned value (the table
        // refreshes via MAIL_LIST_USERS invalidation), but we keep it honest.
        transformResponse: (response: {
          results: MailListMembershipResponse[];
          skippedIds: number[];
        }) => response.results.map(flattenMember),
        invalidatesTags: ["MAIL_LIST_USERS", "MAIL_LISTS", "MAIL_USERS"],
      },
    ),

    removeUsersFromMailList: build.mutation<void, RemoveUsersFromListPayload>({
      query: ({ mailListId, ...body }) => ({
        url: `${MAIL_BASE_URL}/lists/${mailListId}/users/remove/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_result, _error, { mailListId }) => [
        "MAIL_LIST_USERS",
        "MAIL_LISTS",
        "MAIL_USERS",
        { type: "MAIL_LISTS", id: mailListId },
      ],
    }),

    // --- Mail Users ---

    listMailUsers: build.query<
      PaginatedResponse<MailAccount>,
      PaginatedQuery<{
        search?: string;
        exclude_list?: number;
      }>
    >({
      query: (params) => ({
        url: `${MAIL_BASE_URL}/users/`,
        params,
      }),
      transformResponse: (response: PaginatedResponse<MailAccount>) => ({
        ...response,
        results: response.results.map(enrichMailAccount),
      }),
      providesTags: ["MAIL_USERS"],
    }),

    getMailUser: build.query<MailAccount, number>({
      query: (userId) => `${MAIL_BASE_URL}/users/${userId}/`,
      transformResponse: (response: MailAccount) => enrichMailAccount(response),
      providesTags: (_result, _error, userId) => [
        { type: "MAIL_USERS", id: userId },
      ],
    }),

    createMailUser: build.mutation<MailAccount, CreateMailUserPayload>({
      query: (body) => ({
        url: `${MAIL_BASE_URL}/users/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MAIL_USERS"],
    }),

    updateMailUser: build.mutation<MailAccount, UpdateMailUserPayload>({
      query: ({ id, ...body }) => ({
        url: `${MAIL_BASE_URL}/users/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "MAIL_USERS", id },
        "MAIL_USERS",
        "MAIL_LIST_USERS",
      ],
    }),

    deleteMailUser: build.mutation<void, number>({
      query: (userId) => ({
        url: `${MAIL_BASE_URL}/users/${userId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MAIL_USERS", "MAIL_LIST_USERS", "MAIL_LISTS"],
    }),

    // --- Mail User Lists (nested: /users/{id}/lists/) ---

    listMailUserLists: build.query<
      PaginatedResponse<MailList>,
      MailUserListsQuery
    >({
      query: ({ userId, ...params }) => ({
        url: `${MAIL_BASE_URL}/users/${userId}/lists/`,
        params,
      }),
      transformResponse: (response: PaginatedResponse<MailList>) => ({
        ...response,
        results: response.results.map(enrichMailList),
      }),
      providesTags: ["MAIL_USER_LISTS"],
    }),

    addUserToLists: build.mutation<MailList[], AddUserToListsPayload>({
      query: ({ userId, ...body }) => ({
        url: `${MAIL_BASE_URL}/users/${userId}/lists/`,
        method: "POST",
        body,
      }),
      transformResponse: (response: MailList[]) => response.map(enrichMailList),
      invalidatesTags: ["MAIL_USER_LISTS", "MAIL_LIST_USERS", "MAIL_USERS"],
    }),

    // --- Mail Categories ---

    listMailCategories: build.query<
      PaginatedResponse<MailCategory>,
      PaginatedQuery<MailListCategoriesQuery>
    >({
      query: (params) => ({ url: `${MAIL_BASE_URL}/categories/`, params }),
      transformResponse: (response: PaginatedResponse<MailCategory>) => ({
        ...response,
        results: response.results.map(
          (cat) => enrichCategory(cat) as MailCategory,
        ),
      }),
      providesTags: ["MAIL_CATEGORIES"],
    }),

    createMailCategory: build.mutation<MailCategory, CreateMailCategoryPayload>(
      {
        query: (body) => ({
          url: `${MAIL_BASE_URL}/categories/`,
          method: "POST",
          body,
        }),
        transformResponse: (response: MailCategory) =>
          enrichCategory(response) as MailCategory,
        invalidatesTags: ["MAIL_CATEGORIES"],
      },
    ),

    updateMailCategory: build.mutation<MailCategory, UpdateMailCategoryPayload>(
      {
        query: ({ id, ...body }) => ({
          url: `${MAIL_BASE_URL}/categories/${id}/`,
          method: "PATCH",
          body,
        }),
        transformResponse: (response: MailCategory) =>
          enrichCategory(response) as MailCategory,
        invalidatesTags: ["MAIL_CATEGORIES", "MAIL_LISTS"],
      },
    ),

    deleteMailCategory: build.mutation<void, number>({
      query: (categoryId) => ({
        url: `${MAIL_BASE_URL}/categories/${categoryId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MAIL_CATEGORIES", "MAIL_LISTS"],
    }),

    bulkDeleteMailCategories: build.mutation<
      void,
      DeleteBulkMailCategoryPayload
    >({
      query: (body) => ({
        url: `${MAIL_BASE_URL}/categories/remove/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["MAIL_CATEGORIES", "MAIL_LISTS"],
    }),
  }),

  overrideExisting: false,
});

export const {
  // Mail Config
  useGetMailConfigQuery,
  // Mail Lists
  useListMailListsQuery,
  useGetMailListQuery,
  useCreateMailListMutation,
  useUpdateMailListMutation,
  useDeleteMailListMutation,
  // Mail List Users
  useListMailListUsersQuery,
  useAddUsersToMailListMutation,
  useRemoveUsersFromMailListMutation,
  // Mail Users
  useListMailUsersQuery,
  useGetMailUserQuery,
  useCreateMailUserMutation,
  useUpdateMailUserMutation,
  useDeleteMailUserMutation,
  // Mail User Lists
  useListMailUserListsQuery,
  useAddUserToListsMutation,
  // Mail Categories
  useListMailCategoriesQuery,
  useCreateMailCategoryMutation,
  useUpdateMailCategoryMutation,
  useDeleteMailCategoryMutation,
  useBulkDeleteMailCategoriesMutation,
} = mailListApi;
